import Phaser from "phaser";
import type {
  GameEngine,
  GameEvent,
  GameState,
  GameCommand,
  EnemyState,
  TowerState,
  Point,
  ProjectileState,
  DamageType,
} from "@bastion/game-core";
import type { MapConfig, TowerConfig, EnemyConfig } from "@bastion/data";
import { gridCellCentre } from "@bastion/game-core";
import { EffectsSystem } from "./EffectsSystem";
import { assetManifest, assetTextureKey, resolveAssetUrls } from "./assets/manifest";

/** 敌人类型 ID 到其身体半径分数的映射——与 data 包保持一致。 */
const ENEMY_RADIUS_CELLS: Record<string, number> = {};

function isPoint(value: unknown): value is Point {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    "y" in value &&
    typeof value.x === "number" &&
    Number.isFinite(value.x) &&
    typeof value.y === "number" &&
    Number.isFinite(value.y)
  );
}

/**
 * Phaser 是*渲染器*，不是规则所有者（CLAUDE.md §7）。
 *
 *   - 每帧读取 GameState / GameEvent
 *   - 绘制精灵、粒子、补间动画、摄像机
 *   - 将用户输入转发为 GameCommand 给引擎
 *
 * 它从不计算伤害、生成敌人或修改金币。这些都在 @bastion/game-core 中完成；
 * 此包只负责让它们看起来更好。
 */

export interface RendererOptions {
  engine: GameEngine;
  mapConfig: MapConfig;
  towers: Record<string, TowerConfig>;
  /** 技能配置，用于读取炮台技能图标。 */
  skills: Record<string, import("@bastion/data").SkillConfig>;
  enemies?: Record<string, EnemyConfig>;
  /** 是否绘制基地和出生点标记；调试页关闭。 */
  showMapMarkers?: boolean;
  target: HTMLElement;
  /** 当用户请求引擎应处理的操作时回调 */
  dispatch: (command: GameCommand) => void;
  /** 可选；在非建造模式下的画布点击时以地图坐标回调 */
  onTowerClick?: (x: number, y: number) => void;
  /** 可选；点击敌人时以敌人 id 回调 */
  onEnemyClick?: (enemyId: string) => void;
  /** 可选；提供已创建的 effects 系统（或内部自动创建） */
  effects?: EffectsSystem;
}

/**
 * A single Phaser Game + Scene that renders one map's real-time state.
 * The scene is a thin view-model: `syncFromState` is called by the host
 * app every animation frame with the latest GameState.
 */
export class GameRenderer {
  private readonly game: Phaser.Game;
  private readonly scene: GameScene;
  readonly opts: RendererOptions;
  private _pendingSync: GameState | null = null;
  private _pendingEffects: GameEvent[] = [];
  private readonly unsubscribeAreaEffect: () => void;
  private readonly unsubscribeChainEffect: () => void;
  private readonly unsubscribeLightningStrike: () => void;
  private readonly unsubscribeSkillUsed: () => void;
  private readonly unsubscribeGlobalSkillImpact: () => void;
  private readonly unsubscribeGlobalSkillCancelled: () => void;
  private readonly unsubscribeEnemyDamaged: () => void;

  constructor(opts: RendererOptions) {
    this.opts = opts;
    this.scene = new GameScene(opts);
    // 传递 renderer 引用给场景，以便场景创建后回调
    (this.scene as any).renderer = this;
    this.unsubscribeAreaEffect = opts.engine.events.subscribe("area_damage_impact", (event) =>
      this.handleVisualEvent(event),
    );
    this.unsubscribeChainEffect = opts.engine.events.subscribe("chain_lightning", (event) =>
      this.handleVisualEvent(event),
    );
    this.unsubscribeLightningStrike = opts.engine.events.subscribe("lightning_strike", (event) =>
      this.handleVisualEvent(event),
    );
    this.unsubscribeSkillUsed = opts.engine.events.subscribe("skill_used", (event) =>
      this.handleVisualEvent(event),
    );
    this.unsubscribeGlobalSkillImpact = opts.engine.events.subscribe(
      "global_skill_impact",
      (event) => this.handleVisualEvent(event),
    );
    this.unsubscribeGlobalSkillCancelled = opts.engine.events.subscribe(
      "global_skill_cancelled",
      (event) => this.handleVisualEvent(event),
    );
    this.unsubscribeEnemyDamaged = opts.engine.events.subscribe("enemy_damaged", (event) => {
      const enemyId = event.payload?.["enemyId"];
      const damageType = event.payload?.["damageType"];
      if (typeof enemyId === "string" && (damageType === "physical" || damageType === "magic")) {
        this.scene.noteDamageType(enemyId, damageType);
      }
    });
    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: opts.target,
      width: opts.mapConfig.width,
      height: opts.mapConfig.height,
      backgroundColor: "#12151d",
      scene: [this.scene],
      pixelArt: true,
      physics: { default: "arcade", arcade: { debug: false } },
      // 无论设备像素比如何，始终保持画布缓冲为精确的逻辑地图尺寸（800×600）。
      // 若不如此设置，Phaser 会按 DPR 缩放渲染缓冲，导致画布的 CSS 盒不再与
      // `pointer.x/y`（Phaser 的逻辑坐标）1:1 映射，从而在 HiDPI 显示器上
      // 以及无头/E2E 运行时，基于画布的控制（建造放置、塔选择）命中位置偏移。
      scale: {
        mode: Phaser.Scale.NONE,
        autoCenter: Phaser.Scale.NO_CENTER,
      },
    });
  }

  /** 将最新的引擎状态推入场景的视觉模型。 */
  sync(state: GameState): void {
    // 如果场景尚未创建（纹理未加载），则缓存状态等待 create() 回调
    const scene = this.scene as unknown as Record<string, unknown>;
    if (!scene["sceneCreated"]) {
      this._pendingSync = state;
      return;
    }
    this.scene.syncFromState(state, this.opts.engine.time);
    this.scene.effects.syncGlacialStorms(state.activeSkillFields ?? []);
    this.scene.effects.update();
  }

  /** 通知场景已就绪，可以应用缓冲的同步 */
  applyPendingSync(): void {
    if (this._pendingSync) {
      const pending = this._pendingSync;
      this._pendingSync = null;
      this.sync(pending);
    }
    for (const event of this._pendingEffects.splice(0)) this.applyVisualEvent(event);
  }

  private handleVisualEvent(event: GameEvent): void {
    const scene = this.scene as unknown as Record<string, unknown>;
    if (!scene["sceneCreated"]) {
      this._pendingEffects.push(event);
      return;
    }
    this.applyVisualEvent(event);
  }

  /** 处理全局技能使用事件，保持不同技能各自的视觉表现。 */
  private handleSkillUsedEvent(event: GameEvent): void {
    const payload = event.payload;
    if (!payload) return;
    const skillId = payload["skillId"];
    const position = payload["position"];
    if (skillId === "slow_all") return;
    if (skillId !== "area_damage" || !isPoint(position)) return;
    const impactId = payload["impactId"];
    if (typeof impactId !== "string") return;
    this.scene.effects.spawnMeteorStrike(impactId, position.x, position.y, 0);
  }

  private applyVisualEvent(event: GameEvent): void {
    const payload = event.payload;
    if (!payload) return;
    if (event.type === "global_skill_impact" || event.type === "global_skill_cancelled") {
      const impactId = payload["impactId"];
      if (typeof impactId !== "string") return;
      this.scene.effects.finishMeteorStrike(
        impactId,
        event.type === "global_skill_impact",
        this.opts.skills["area_damage"]?.radius ?? 150,
      );
      return;
    }
    if (event.type === "skill_used") {
      this.handleSkillUsedEvent(event);
      return;
    }
    if (event.type === "area_damage_impact") {
      const position = payload["position"];
      const radius = payload["radius"];
      if (
        typeof position === "object" &&
        position !== null &&
        "x" in position &&
        "y" in position &&
        typeof position.x === "number" &&
        Number.isFinite(position.x) &&
        typeof position.y === "number" &&
        Number.isFinite(position.y) &&
        typeof radius === "number" &&
        Number.isFinite(radius) &&
        radius > 0
      ) {
        this.scene.effects.spawnAreaImpact(position.x, position.y, radius);
      }
    } else if (event.type === "chain_lightning") {
      const origin = payload["origin"];
      const targets = payload["targets"];
      if (!isPoint(origin) || !Array.isArray(targets)) return;
      const validTargets: Point[] = [];
      for (const target of targets) {
        if (!isPoint(target)) return;
        validTargets.push({ x: target.x, y: target.y });
      }
      this.scene.effects.spawnChainLightning({ x: origin.x, y: origin.y }, validTargets);
    } else if (event.type === "lightning_strike") {
      const origin = payload["origin"];
      const target = payload["target"];
      if (isPoint(origin) && isPoint(target)) {
        this.scene.effects.spawnLightningStrike(
          { x: origin.x, y: origin.y },
          { x: target.x, y: target.y },
        );
      }
    }
  }

  /**
   * 批量渲染辅助：调用一次 `sync` 然后强制执行一次 Phaser 渲染刷新。
   * 当宿主在一个帧中有多个状态变更需要应用时很有用（例如批量模拟步之后）。
   */
  syncAndFlush(state: GameState): void {
    this.scene.syncFromState(state, this.opts.engine.time);
    this.scene.effects.syncGlacialStorms(state.activeSkillFields ?? []);
    this.scene.effects.syncMeteorStrikes(state.pendingOrbitalStrikes ?? []);
  }

  /** 开始建造特定类型的塔（由 UI 建造面板驱动）。 */
  setBuildMode(mode: { kind: "tower" | "enemy"; type: string } | null): void {
    this.scene.setBuildMode(mode);
  }

  /**
   * 更新放置幽灵的视觉以匹配当前选中塔类型的身体半径。
   * 当用户在建造模式切换塔类型时调用。
   */
  setPlacementRadius(towerType: string | null): void {
    this.scene.setPlacementRadius(towerType);
  }

  /**
   * 在逻辑地图坐标中更新放置幽灵的位置。
   * 场景执行与指针放置相同的网格吸附逻辑。
   */
  setPlacementPosition(position: { x: number; y: number } | null): void {
    this.scene.setPlacementPosition(position);
  }

  /** 开始技能瞄准模式：在画布上显示范围指示圈，点击触发技能。 */
  startSkillAim(skillId: string): void {
    this.scene.startSkillAim(skillId);
  }

  /** 取消技能瞄准模式。 */
  cancelSkillAim(): void {
    this.scene.exitSkillAimMode();
  }

  /** 高亮特定塔的射程/选中状态（由 UI 驱动）。 */
  selectTower(towerId: string | null): void {
    this.scene.selectTower(towerId);
  }

  /**
   * Phaser 启动时 config 中列出的场景处于 *sleeping* 状态，
   * 因此它们的 `create()`（地图绘制 + 输入绑定）尚未运行。
   * 唤醒场景以触发 `create()`。若已唤醒则不操作。
   */
  wakeActiveScene(): void {
    if (this.scene.sys.isActive()) return;
    this.game.scene.wake("game");
  }

  /** 设置非建造模式画布点击时调用的回调。 */
  set onTowerClick(cb: ((x: number, y: number) => void) | null) {
    this.scene.onTowerClick = cb;
  }
  /** 接收选中塔的数据，用于渲染操作按钮。 */
  onTowerSelected(data: {
    id: string;
    type: string;
    position: Point;
    buildCost: number;
    upgradeCosts: number[];
    level: number;
  }): void {
    (this.scene as any).selectedTowerActionData = data;
  }
  set onEnemyClick(cb: ((enemyId: string) => void) | null) {
    this.scene.onEnemyClick = cb;
  }
  selectEnemy(enemyId: string | null): void {
    // 选敌人时退出技能瞄准模式
    this.cancelSkillAim();
    this.scene.selectEnemy(enemyId);
  }

  destroy(): void {
    this.unsubscribeAreaEffect();
    this.unsubscribeChainEffect();
    this.unsubscribeLightningStrike();
    this.unsubscribeSkillUsed();
    this.unsubscribeGlobalSkillImpact();
    this.unsubscribeGlobalSkillCancelled();
    this.unsubscribeEnemyDamaged();
    this._pendingEffects = [];
    this.scene.destroyScene();
    this.scene.effects.destroy();
    this.game.destroy(true);
  }
}

/**
 * A Phaser Scene is the view-model. We avoid the name `renderer` on any
 * scene field because Phaser's own `Scene` type already declares a
 * `renderer: CanvasRenderer | WebGLRenderer` property that would collide.
 */
class GameScene extends Phaser.Scene {
  private readonly enemies = new Map<string, Phaser.GameObjects.Container>();
  private readonly enemyHpBars = new Map<string, Phaser.GameObjects.Rectangle>();
  private readonly enemyHpBarBgs = new Map<string, Phaser.GameObjects.Rectangle>();
  private readonly towers = new Map<string, Phaser.GameObjects.Container>();
  private readonly laserBeams = new Map<
    string,
    { graphics: Phaser.GameObjects.Graphics; phase: number }
  >();
  private readonly towerRangeCircles = new Map<string, Phaser.GameObjects.Arc>();
  /** 选中塔时显示的操作按钮（升级/出售）。 */
  private readonly towerActionButtons = new Map<string, Phaser.GameObjects.Container>();
  /** 按钮点击检测区域。 */
  private readonly towerButtonHitZones = new Map<string, Phaser.Geom.Rectangle>();
  /** 炮台技能就绪时的发光效果，按塔 id 索引。 */
  private readonly towerSkillGlows = new Map<string, Phaser.GameObjects.Arc>();
  /** 追踪每个塔是否有可释放技能，用于控制发光脉动。 */
  private readonly towerSkillReady = new Map<string, boolean>();
  /** 当前屏幕上可见的投射物。 */
  private projectiles = new Map<string, Phaser.GameObjects.Arc>();
  /** 可复用的投射物池——返回此处的对象在下次 sync 时被隐藏并复用。 */
  private readonly projectilePool: Phaser.GameObjects.Arc[] = [];
  /** 冰锥投射物轨迹线——在上一帧位置和当前帧位置之间绘制冰晶拖尾。 */
  private readonly iceConeTrails = new Map<string, Phaser.GameObjects.Graphics>();
  /** 可复用的冰锥轨迹图形池。 */
  private readonly iceConeTrailPool: Phaser.GameObjects.Graphics[] = [];

  private buildMode: { kind: "tower" | "enemy"; type: string } | null = null;
  /** 技能瞄准模式：用户点击选择技能落点时处于此状态。 */
  private skillAimState: {
    skillId: string;
    config: import("@bastion/data").SkillConfig;
  } | null = null;
  /** 技能瞄准范围指示圈 */
  private skillAimCircle: Phaser.GameObjects.Arc | null = null;
  private placementGhost: Phaser.GameObjects.Container | null = null;
  private selectedTowerId: string | null = null;
  private selectedEnemyId: string | null = null;
  /** 当前选中塔的操作按钮数据。 */
  selectedTowerActionData: {
    id: string;
    type: string;
    position: Point;
    buildCost: number;
    upgradeCosts: number[];
    level: number;
  } | null = null;
  private inputAttached = false;
  private sceneCreated = false;
  private gridOverlay: Phaser.GameObjects.Rectangle[] | null = null;
  private enemySelectionCircle: Phaser.GameObjects.Arc | null = null;
  /** 每个实体的身体半径——用于按比例命中区域点击。 */
  private readonly enemyBodyRadii = new Map<string, number>();
  private readonly towerBodyRadii = new Map<string, number>();
  /** 预算：同一时间最多保持存活的投射物视觉数量。 */
  private static readonly PROJECTILE_POOL_BUDGET = 128;
  private static readonly PROJECTILE_RADIUS = 5;
  private static readonly ICE_CONE_PROJECTILE_RADIUS = 10;

  private readonly previousProjectiles = new Map<
    string,
    {
      position: Point;
      attackMode?: ProjectileState["attackMode"];
      impactRadius?: number;
    }
  >();

  private readonly mapConfig: MapConfig;
  private readonly towersConfig: Record<string, TowerConfig>;
  private readonly skillsConfig: Record<string, import("@bastion/data").SkillConfig>;
  private readonly enemiesConfig: Record<string, EnemyConfig>;
  private readonly towerSkinTextureKeys: Record<string, string>;
  private readonly enemySkinTextureKeys: Record<string, string>;
  private readonly towerSkinUrls: Record<string, string>;
  private readonly enemySkinUrls: Record<string, string>;
  private readonly showMapMarkers: boolean;
  private readonly dispatch: (command: GameCommand) => void;
  onTowerClick: ((x: number, y: number) => void) | null = null;
  onEnemyClick: ((enemyId: string) => void) | null = null;
  /** 场景激活后创建的特效系统。 */
  readonly effects: EffectsSystem;
  /** 每帧最后看到的敌人 id——用于检测 tick 之间的死亡。 */
  private prevEnemyIds = new Set<string>();
  /** 每帧最后看到的敌人 HP——用于检测伤害以显示浮动数字。 */
  private readonly prevEnemyHp = new Map<string, number>();
  /** 本帧收到的敌人伤害类型；每个敌人保留最后一笔命中的伤害类型。 */
  private readonly damageTypesByEnemy = new Map<string, DamageType>();
  /** 追踪已升级的塔 id，避免重复触发闪光效果。 */
  private readonly upgradedTowers = new Set<string>();

  constructor(opts: RendererOptions) {
    super({ key: "game" });
    this.mapConfig = opts.mapConfig;
    this.towersConfig = opts.towers;
    this.skillsConfig = opts.skills;
    this.enemiesConfig = opts.enemies ?? {};
    this.towerSkinTextureKeys = Object.fromEntries(
      Object.keys(assetManifest.towers).map((id) => [id, assetTextureKey("tower", id)]),
    );
    this.enemySkinTextureKeys = Object.fromEntries(
      Object.keys(assetManifest.enemies).map((id) => [id, assetTextureKey("enemy", id)]),
    );
    const resolvedAssetUrls = resolveAssetUrls(
      assetManifest,
      typeof document === "undefined" ? "" : document.baseURI,
    );
    this.towerSkinUrls = Object.fromEntries(
      Object.keys(assetManifest.towers).map((id) => [
        id,
        resolvedAssetUrls[assetTextureKey("tower", id)],
      ]),
    );
    this.enemySkinUrls = Object.fromEntries(
      Object.keys(assetManifest.enemies).map((id) => [
        id,
        resolvedAssetUrls[assetTextureKey("enemy", id)],
      ]),
    );
    this.showMapMarkers = opts.showMapMarkers ?? true;
    this.dispatch = opts.dispatch;
    this.onTowerClick = opts.onTowerClick ?? null;
    this.onEnemyClick = opts.onEnemyClick ?? null;
    this.effects = opts.effects ?? new EffectsSystem(this);
  }

  preload(): void {
    for (const [id, textureKey] of Object.entries(this.towerSkinTextureKeys)) {
      const url = this.towerSkinUrls[id];
      if (url) this.load.image(textureKey, url);
    }
    for (const [id, textureKey] of Object.entries(this.enemySkinTextureKeys)) {
      const url = this.enemySkinUrls[id];
      if (url) this.load.image(textureKey, url);
    }
  }

  create(): void {
    this.sceneCreated = true;
    // 怪物插画使用平滑采样，避免缩小显示时产生像素化边缘。
    for (const textureKey of Object.values(this.enemySkinTextureKeys)) {
      if (this.textures.exists(textureKey)) {
        this.textures.get(textureKey).setFilter(Phaser.Textures.FilterMode.LINEAR);
      }
    }
    this.drawMap();
    this.attachInput();
    // 始终绘制网格叠加层
    this.gridOverlay = this.drawGrid(this.mapConfig);
    if (this.buildMode) this.applyBuildMode();
    // 场景创建完成后，通知 GameRenderer 可以开始同步
    // 这确保了纹理加载完成后再渲染实体
    const renderer = (this as any).renderer;
    if (renderer && typeof (renderer as any).applyPendingSync === "function") {
      (renderer as any).applyPendingSync();
    }
  }

  /** 绘制静态地图几何：基地、出生标记。 */
  private drawMap(): void {
    if (!this.showMapMarkers) return;
    const base = this.mapConfig.base;
    const baseRect = this.add.rectangle(base.x, base.y, 40, 40, 0x3b82f6, 1);
    baseRect.setDepth(-10).setStrokeStyle(2, 0x93c5fd, 1);
    this.add
      .text(base.x, base.y, "🏰", {
        fontSize: "22px",
        fontFamily: "sans-serif",
      })
      .setOrigin(0.5)
      .setDepth(-10);

    for (const sp of this.mapConfig.spawnPoints) {
      const spawnCircle = this.add.circle(sp.x, sp.y, 18, 0xef4444, 0.9);
      spawnCircle.setDepth(-10).setStrokeStyle(2, 0x7f1d1d, 1);
      this.add
        .text(sp.x, sp.y, "☠", { fontSize: "16px", color: "#fff" })
        .setOrigin(0.5)
        .setDepth(-10);
    }
  }

  private pointerToMap(pointer: Phaser.Input.Pointer): Point {
    const canvas = this.sys.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const event = pointer.event;
    const clientPoint =
      "clientX" in event
        ? { x: event.clientX, y: event.clientY }
        : event.changedTouches?.[0]
          ? { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY }
          : { x: rect.left + pointer.x, y: rect.top + pointer.y };

    return {
      x: ((clientPoint.x - rect.left) / rect.width) * this.mapConfig.width,
      y: ((clientPoint.y - rect.top) / rect.height) * this.mapConfig.height,
    };
  }

  private attachInput(): void {
    if (this.inputAttached) return;
    this.inputAttached = true;
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      const pos = this.pointerToMap(pointer);

      // 技能瞄准模式：点击即发射
      if (this.skillAimState) {
        this.fireSkillAim(pos);
        return;
      }

      // 优先处理点击现有实体（选中敌人/塔），即使处于建造模式也应响应。
      if (this.onEnemyClick) {
        let hitEnemy = false;
        for (const [id, container] of this.enemies) {
          const dx = pos.x - container.x;
          const dy = pos.y - container.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const bodyRadius = this.enemyBodyRadii.get(id) ?? this.mapConfig.gridSize * 0.5;
          if (dist < 2 * bodyRadius) {
            this.onEnemyClick(id);
            hitEnemy = true;
            break;
          }
        }
        if (hitEnemy) return;
      }

      // 检查操作按钮点击（有选中塔时）
      if (this.selectedTowerId) {
        for (const [btnName, rect] of this.towerButtonHitZones) {
          if (Phaser.Geom.Rectangle.Contains(rect, pos.x, pos.y)) {
            if (btnName === "upgrade") {
              this.dispatch({ type: "upgrade_tower", towerId: this.selectedTowerId });
            } else if (btnName === "sell") {
              this.dispatch({ type: "sell_tower", towerId: this.selectedTowerId });
              this.selectTower(null);
            }
            return;
          }
        }
      }

      if (this.buildMode?.kind === "tower") {
        try {
          this.dispatch({
            type: "build_tower",
            towerType: this.buildMode.type,
            position: pos,
          });
        } catch (err) {
          console.warn("[renderer] build_tower rejected:", err);
        }
      } else if (this.buildMode?.kind === "enemy") {
        try {
          this.dispatch({
            type: "build_enemy_debug",
            enemyType: this.buildMode.type,
            position: pos,
          });
        } catch (err) {
          console.warn("[renderer] build_enemy_debug rejected:", err);
        }
      } else if (this.onTowerClick) {
        this.onTowerClick?.(pos.x, pos.y);
      }
    });
  }

  setBuildMode(mode: { kind: "tower" | "enemy"; type: string } | null): void {
    this.buildMode = mode;
    if (!this.sceneCreated) return;
    this.applyBuildMode();
  }

  private applyBuildMode(): void {
    if (!this.sceneCreated) return;
    this.input.off("pointermove", this.onGhostMove);
    this.placementGhost?.destroy();
    this.placementGhost = null;
    const mode = this.buildMode;
    if (mode) {
      const isTower = mode.kind === "tower";
      const config = isTower ? this.towersConfig[mode.type] : this.enemiesConfig[mode.type];
      if (config) {
        const radiusCells =
          config.bodyRadiusCells ?? (isTower ? 0.5 : (config as EnemyConfig).isBoss ? 0.85 : 0.5);
        const bodyRadius = (radiusCells * this.mapConfig.gridSize) / 2;
        this.placementGhost = this.add.container(0, 0);
        if (isTower) {
          const towerConfig = config as TowerConfig;
          const ghostRange = this.add.circle(0, 0, towerConfig.range, 0x60a5fa, 0.08);
          ghostRange.setStrokeStyle(1, 0x60a5fa, 0.4);
          this.placementGhost.add(ghostRange);
        }
        const ghostBody = this.add.circle(0, 0, bodyRadius, isTower ? 0x60a5fa : 0xf87171, 0.4);
        ghostBody.setStrokeStyle(2, isTower ? 0x60a5fa : 0xf87171, 0.9);
        this.placementGhost.add(ghostBody);
        this.placementGhost.setPosition(0, 0).setVisible(false);
        this.input.on("pointermove", this.onGhostMove);
        this.input.setDefaultCursor("crosshair");
      } else {
        console.warn("[renderer] unknown build type:", mode.type);
      }
    } else {
      this.input.setDefaultCursor("default");
    }
  }

  /** 更新放置幽灵以匹配当前选中的塔类型的身体半径和射程。 */
  setPlacementRadius(towerType: string | null): void {
    if (!towerType) return;
    this.buildMode = { kind: "tower", type: towerType };
    if (!this.sceneCreated) return;
    this.applyBuildMode();
  }

  /** 将放置幽灵移动到逻辑地图坐标。 */
  setPlacementPosition(position: { x: number; y: number } | null): void {
    if (!this.placementGhost) return;
    if (!position) {
      this.placementGhost.setVisible(false);
      return;
    }
    this.movePlacementGhost(position.x, position.y);
  }

  /** 绘制覆盖整个地图的淡色网格叠加层。返回矩形以便稍后销毁。 */
  private drawGrid(map: MapConfig): Phaser.GameObjects.Rectangle[] {
    this.gridOverlay?.forEach((r) => r.destroy());
    this.gridOverlay = [];
    const { width, height, gridSize } = map;
    const colCount = Math.floor(width / gridSize);
    const rowCount = Math.floor(height / gridSize);
    for (let gx = 0; gx <= colCount; gx++) {
      const x = gx * gridSize;
      const line = this.add.rectangle(x, height / 2, 1, height, 0xffffff, 0.06).setDepth(-9);
      this.gridOverlay!.push(line);
    }
    for (let gy = 0; gy <= rowCount; gy++) {
      const y = gy * gridSize;
      const line = this.add.rectangle(width / 2, y, width, 1, 0xffffff, 0.06).setDepth(-9);
      this.gridOverlay!.push(line);
    }
    return this.gridOverlay;
  }

  private onGhostMove = (pointer: Phaser.Input.Pointer): void => {
    const position = this.pointerToMap(pointer);
    this.movePlacementGhost(position.x, position.y);
  };

  private movePlacementGhost(x: number, y: number): void {
    if (!this.placementGhost) return;
    const snapped = gridCellCentre(
      Math.floor(x / this.mapConfig.gridSize),
      Math.floor(y / this.mapConfig.gridSize),
      this.mapConfig.gridSize,
    );
    this.placementGhost.setPosition(snapped.x, snapped.y).setVisible(true);
  }

  selectTower(towerId: string | null): void {
    this.selectedTowerId = towerId;
    this.selectedEnemyId = null;
    this.enemySelectionCircle?.destroy();
    this.enemySelectionCircle = null;
    for (const [id, circle] of this.towerRangeCircles) {
      circle.setVisible(id === towerId);
    }
    // 更新操作按钮显示
    this.updateTowerActionButtons(towerId);
  }

  /** 开始技能瞄准模式：显示范围指示圈，等待用户点击落点。 */
  startSkillAim(skillId: string): void {
    // 如果技能没有 radius，无法展示瞄准圈，直接退出
    const config = this.skillsConfig[skillId];
    if (!config || config.radius == null) return;

    // 退出已有的瞄准模式
    this.exitSkillAimMode();

    this.skillAimState = { skillId, config };
    this.input.setDefaultCursor("crosshair");
    // 跟随指针移动瞄准圈
    this.input.on("pointermove", this.onSkillAimMove);
  }

  /** 取消技能瞄准模式，恢复默认光标。 */
  exitSkillAimMode(): void {
    if (this.skillAimState) {
      this.skillAimCircle?.destroy();
      this.skillAimCircle = null;
      this.skillAimState = null;
    }
    this.input.off("pointermove", this.onSkillAimMove);
    // 若当前仍处于建造模式，恢复建造光标；否则恢复默认
    if (this.buildMode) {
      this.input.setDefaultCursor("crosshair");
    } else {
      this.input.setDefaultCursor("default");
    }
  }

  /** 指针移动时更新瞄准圈位置。 */
  private onSkillAimMove = (pointer: Phaser.Input.Pointer): void => {
    if (!this.skillAimState) return;
    const pos = this.pointerToMap(pointer);
    const { config } = this.skillAimState;
    if (!this.skillAimCircle) {
      this.skillAimCircle = this.add
        .circle(pos.x, pos.y, config.radius!, 0xff6b35, 0.12)
        .setStrokeStyle(2, 0xff6b35, 0.7)
        .setDepth(10);
    } else {
      this.skillAimCircle.setPosition(pos.x, pos.y);
    }
  };

  /** 点击画布时发射技能（瞄准模式下生效）。 */
  private fireSkillAim(pos: Point): void {
    if (!this.skillAimState) return;
    const { skillId } = this.skillAimState;
    // 释放前销毁瞄准圈
    this.skillAimCircle?.destroy();
    this.skillAimCircle = null;
    this.skillAimState = null;
    this.input.off("pointermove", this.onSkillAimMove);
    this.input.setDefaultCursor("default");
    // 发射技能（position 由用户点击位置决定）
    try {
      this.dispatch({ type: "use_skill", skillId, position: pos });
    } catch (err) {
      console.warn("[renderer] use_skill rejected:", err);
    }
  }

  /** 为选中的塔创建/更新右侧（升级）和下方（出售）的圆形按钮。 */
  private updateTowerActionButtons(towerId: string | null, gold?: number): void {
    // 清除所有现有按钮
    for (const [, c] of this.towerActionButtons) c.destroy();
    this.towerActionButtons.clear();
    this.towerButtonHitZones.clear();

    if (!towerId) return;

    // 使用 renderer 传入的数据
    const data = this.selectedTowerActionData;
    if (!data || data.id !== towerId) return;

    const config = this.towersConfig[data.type];
    if (!config) return;

    // 获取塔的位置（使用数据中的位置，因为此时 container 可能还未创建）
    const gx = Math.floor(data.position.x / this.mapConfig.gridSize);
    const gy = Math.floor(data.position.y / this.mapConfig.gridSize);
    // 格子中心坐标
    const cx = (gx + 0.5) * this.mapConfig.gridSize;
    const cy = (gy + 0.5) * this.mapConfig.gridSize;

    const gridSize = this.mapConfig.gridSize;
    // 按钮大小 28px，圆心在格子中心
    const btnSize = 28;
    const halfBtn = btnSize / 2;

    // 计算升级费用
    const nextUpgrade = config.upgrades.find((u) => u.level === data.level + 1);
    const upgradeCost = nextUpgrade?.upgradeCost ?? null;

    // 检查金币是否足够
    const canAfford = !upgradeCost || (gold ?? 0) >= upgradeCost;

    // 升级按钮：右侧格子中心
    const upgradeX = cx + gridSize;
    const upgradeY = cy;
    // 满级或金币不足时半透明灰色
    const upgradeAlpha = upgradeCost === null ? 0.35 : canAfford ? 0.5 : 0.3;
    const upgradeColor = upgradeCost === null ? 0x888888 : canAfford ? 0x22c55e : 0x888888;
    const upgradeCircle = this.add
      .circle(0, 0, halfBtn, upgradeColor, upgradeAlpha)
      .setStrokeStyle(2, 0x14532d, 0.6);
    const upgradeText = this.add
      .text(0, 0, upgradeCost === null ? "MAX" : String(upgradeCost), {
        fontSize: "11px",
        color: "#fff",
        fontFamily: "monospace",
        fontStyle: "bold",
      })
      .setOrigin(0.5);
    const upgradeContainer = this.add.container(upgradeX, upgradeY, [upgradeCircle, upgradeText]);
    upgradeContainer.setDepth(10);
    this.towerActionButtons.set("upgrade", upgradeContainer);
    this.towerButtonHitZones.set(
      "upgrade",
      new Phaser.Geom.Rectangle(upgradeX - halfBtn, upgradeY - halfBtn, btnSize, btnSize),
    );

    // 出售按钮：下方格子中心
    const sellX = cx;
    const sellY = cy + gridSize;
    const sellValue = Math.round(
      data.buildCost + 0.7 * data.upgradeCosts.reduce((a: number, b: number) => a + b, 0),
    );
    const sellCircle = this.add
      .circle(0, 0, halfBtn, 0xef4444, 0.5)
      .setStrokeStyle(2, 0x7f1d1d, 0.6);
    const sellText = this.add
      .text(0, 0, String(sellValue), {
        fontSize: "11px",
        color: "#fff",
        fontFamily: "monospace",
        fontStyle: "bold",
      })
      .setOrigin(0.5);
    const sellContainer = this.add.container(sellX, sellY, [sellCircle, sellText]);
    sellContainer.setDepth(10);
    this.towerActionButtons.set("sell", sellContainer);
    this.towerButtonHitZones.set(
      "sell",
      new Phaser.Geom.Rectangle(sellX - halfBtn, sellY - halfBtn, btnSize, btnSize),
    );
  }

  /** 每帧同步时更新操作按钮（处理金币变化、升级等）。 */
  private syncActionButtons(state: GameState): void {
    if (!this.selectedTowerId) return;
    // 检查塔是否还存在
    const tower = state.towers.find((t: any) => t.id === this.selectedTowerId);
    if (!tower) {
      this.updateTowerActionButtons(null);
      return;
    }
    // 如果数据不匹配，从当前状态重新获取
    if (
      !this.selectedTowerActionData ||
      this.selectedTowerActionData.id !== this.selectedTowerId ||
      this.selectedTowerActionData.level !== tower.level ||
      this.selectedTowerActionData.upgradeCosts.length !== tower.upgradeCosts.length
    ) {
      this.selectedTowerActionData = {
        id: tower.id,
        type: tower.type,
        position: tower.position,
        buildCost: tower.buildCost,
        upgradeCosts: [...tower.upgradeCosts],
        level: tower.level,
      };
    }
    // 传入最新金币数
    this.updateTowerActionButtons(this.selectedTowerId, state.gold);
  }

  /** 移除上一次选中的高亮效果 */
  selectEnemy(enemyId: string | null): void {
    // 移除之前的选中高亮
    this.enemySelectionCircle?.destroy();
    this.enemySelectionCircle = null;
    this.selectedEnemyId = enemyId;
    // 清除塔操作按钮
    this.selectedTowerId = null;
    this.updateTowerActionButtons(null);

    // 高亮选中的敌人（闪光效果 + 选中圆圈）
    if (enemyId !== null) {
      const container = this.enemies.get(enemyId);
      if (container) {
        // 闪光效果
        this.tweens.add({
          targets: container,
          alpha: 0.5,
          duration: 100,
          yoyo: true,
          repeat: 1,
        });
        // 添加选中圆圈
        this.enemySelectionCircle = this.add.circle(container.x, container.y, 35, 0x60a5fa, 0.15);
        this.enemySelectionCircle.setStrokeStyle(2, 0x60a5fa, 0.8);
      }
    }
  }

  /** 将场景的视觉实体与最新 GameState 进行协调。 */
  noteDamageType(enemyId: string, damageType: DamageType): void {
    this.damageTypesByEnemy.set(enemyId, damageType);
  }

  syncFromState(state: GameState, gameTime: number): void {
    this.reconcileEnemies(state.enemies, state.projectiles);
    this.effects.syncGlacialStorms(state.activeSkillFields ?? []);
    this.effects.syncMeteorStrikes(state.pendingOrbitalStrikes ?? []);
    this.reconcileTowers(state.towers);
    this.updateHpBars(state.enemies);
    // GameState.time 未随 Tick 更新；特效相位使用引擎模拟时间以保持推进和暂停冻结。
    this.reconcileLaserBeams(state.towers, state.enemies, gameTime);
    this.reconcileProjectiles(state);
    // 更新选中敌人的高亮位置
    if (this.selectedEnemyId) {
      const enemy = state.enemies.find((e) => e.id === this.selectedEnemyId);
      if (enemy) {
        const container = this.enemies.get(enemy.id);
        if (container && this.enemySelectionCircle) {
          this.enemySelectionCircle.setPosition(container.x, container.y);
          this.enemySelectionCircle.setVisible(true);
        }
      } else {
        // 敌人已不存在，清除选中
        this.enemySelectionCircle?.destroy();
        this.enemySelectionCircle = null;
        this.selectedEnemyId = null;
      }
    }
    if (this.selectedTowerId) {
      const tower = state.towers.find((t) => t.id === this.selectedTowerId);
      if (tower) {
        const config = this.towersConfig[tower.type];
        if (config) {
          const existing = this.towerRangeCircles.get(tower.id);
          if (existing) {
            existing.setPosition(tower.position.x, tower.position.y);
            existing.setVisible(true);
          } else {
            const circle = this.add.circle(
              tower.position.x,
              tower.position.y,
              config.range,
              0x60a5fa,
              0.08,
            );
            circle.setStrokeStyle(1, 0x60a5fa, 0.5);
            circle.setVisible(true);
            this.towerRangeCircles.set(tower.id, circle);
          }
        }
      }
    }
    // 每帧更新操作按钮（处理金币变化、升级等）
    this.syncActionButtons(state);
  }

  // ─── 特效协调 ──────────────────────────────────────────────────

  private triggerDeathEffects(enemies: EnemyState[]): void {
    const seenIds = new Set<string>();
    for (const enemy of enemies) seenIds.add(enemy.id);

    for (const id of this.prevEnemyIds) {
      if (!seenIds.has(id)) {
        const container = this.enemies.get(id);
        if (container) {
          const x = container.x;
          const y = container.y;
          const enemy = enemies.find((e) => e.id === id);
          const isBoss = enemy?.isBoss === true;
          this.effects.spawnDeathEffect(x, y, isBoss);
        }
      }
    }

    // 本帧 HP 下降的敌人的伤害数字。
    for (const enemy of enemies) {
      const prevHp = this.prevEnemyHp.get(enemy.id);
      if (prevHp !== undefined && prevHp > enemy.hp) {
        const damage = prevHp - enemy.hp;
        this.effects.spawnDamageNumber(
          enemy.position.x,
          enemy.position.y - 20,
          damage,
          this.damageTypesByEnemy.get(enemy.id) ?? "physical",
        );
      }
      this.prevEnemyHp.set(enemy.id, enemy.hp);
    }

    this.damageTypesByEnemy.clear();
    this.prevEnemyIds = seenIds;
  }

  // ─── 敌人协调 ───────────────────────────────────────────────────

  /** 更新炮台技能就绪时的发光脉动效果。 */
  private updateSkillIndicator(container: Phaser.GameObjects.Container, tower: TowerState): void {
    const config = this.towersConfig[tower.type];
    const skillIds = config?.skillIds ?? [];
    const hasReadySkill = skillIds.some((skillId) => {
      const skill = this.skillsConfig[skillId];
      if (!skill) return false;
      const condition = skill.releaseCondition;
      const ready =
        condition?.kind === "attack_count" &&
        (tower.skillReleaseProgress[skillId] ?? 0) >= condition.count;
      const pending = tower.pendingAttackSkillId === skillId;
      const active = tower.attackSpeedBoosts.some(
        (boost) => boost.skillId === skillId && boost.remaining > 0,
      );
      return ready || pending || active;
    });

    const existing = this.towerSkillGlows.get(tower.id);
    const prevReady = this.towerSkillReady.get(tower.id) ?? false;

    if (hasReadySkill) {
      this.towerSkillReady.set(tower.id, true);
      if (!existing) {
        const bodyRadius = this.towerBodyRadii.get(tower.id) ?? 20;
        const glow = this.add
          .circle(0, 0, bodyRadius * 1.6, 0xffd700, 0.25)
          .setStrokeStyle(2, 0xffd700, 0.6)
          .setDepth(1);
        container.add(glow);
        this.towerSkillGlows.set(tower.id, glow);
        // 启动脉动动画
        this.tweens.add({
          targets: glow,
          scaleX: { from: 1, to: 1.25 },
          scaleY: { from: 1, to: 1.25 },
          alpha: { from: 0.25, to: 0.55 },
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });
      } else {
        existing.setVisible(true);
      }
    } else {
      this.towerSkillReady.set(tower.id, false);
      if (existing && prevReady) {
        // 刚消失时保留一小段时间让动画自然淡出
        this.tweens.add({
          targets: existing,
          alpha: 0,
          duration: 300,
          onComplete: () => {
            existing.setVisible(false);
          },
        });
      } else if (existing) {
        existing.setVisible(false);
      }
    }
  }

  private reconcileEnemies(enemies: EnemyState[], _projectiles: ProjectileState[]): void {
    this.triggerDeathEffects(enemies);

    const seen = new Set<string>();
    for (const enemy of enemies) {
      seen.add(enemy.id);
      let container = this.enemies.get(enemy.id);
      if (!container) {
        container = this.createEnemyVisual(enemy);
        this.enemies.set(enemy.id, container);
      }
      container.setPosition(enemy.position.x, enemy.position.y);
    }
    for (const [id, container] of this.enemies) {
      if (!seen.has(id)) {
        container.destroy();
        this.enemies.delete(id);
        this.enemyHpBars.delete(id);
        this.enemyHpBarBgs.delete(id);
        this.prevEnemyHp.delete(id);
        this.enemyBodyRadii.delete(id);
      }
    }
  }

  private createEnemyVisual(enemy: EnemyState): Phaser.GameObjects.Container {
    const isBoss = enemy.isBoss === true;
    const radiusCells =
      ENEMY_RADIUS_CELLS[enemy.type] ??
      this.enemiesConfig[enemy.type]?.bodyRadiusCells ??
      (isBoss ? 0.85 : 0.5);
    const radius = (radiusCells * this.mapConfig.gridSize) / 2;
    const textureKey = this.enemySkinTextureKeys[enemy.type];
    const body =
      textureKey && this.textures.exists(textureKey)
        ? this.add.image(0, 0, textureKey)
        : this.add.circle(0, 0, radius, isBoss ? 0xdc2626 : 0xf87171, 1);
    if (body instanceof Phaser.GameObjects.Image) {
      const bossVisualScale = isBoss ? 1.2 : 1;
      body.setScale(((radius * 2.8) / body.height) * bossVisualScale);
    } else {
      body.setStrokeStyle(1, 0x0f172a, 1);
    }
    const barWidth = radius * 2.2;
    const visualHalfHeight =
      body instanceof Phaser.GameObjects.Image ? body.displayHeight / 2 : radius;
    const barY = -visualHalfHeight - 6;
    const hpBarBg = this.add.rectangle(0, barY, barWidth, 4, 0x000000, 0.6);
    const hpBar = this.add.rectangle(0, barY, barWidth, 4, 0x22c55e, 1);
    const container = this.add.container(0, 0, [body, hpBarBg, hpBar]).setDepth(0);
    this.enemyBodyRadii.set(enemy.id, radius);
    this.enemyHpBars.set(enemy.id, hpBar);
    this.enemyHpBarBgs.set(enemy.id, hpBarBg);
    return container;
  }

  private updateHpBars(enemies: EnemyState[]): void {
    for (const enemy of enemies) {
      const bar = this.enemyHpBars.get(enemy.id);
      if (!bar) continue;
      const fraction = enemy.maxHp > 0 ? enemy.hp / enemy.maxHp : 0;
      const color = fraction > 0.5 ? 0x22c55e : fraction > 0.25 ? 0xeab308 : 0xef4444;
      bar.setFillStyle(color, 1);
      const fullWidth = this.enemyHpBarBgs.get(enemy.id)?.width ?? bar.width;
      bar.setSize(fullWidth * fraction, bar.height);
      bar.setX(-(fullWidth / 2) * (1 - fraction));
    }
  }

  // ─── 塔协调 ───────────────────────────────────────────────────

  private reconcileTowers(towers: TowerState[]): void {
    const seen = new Set<string>();
    for (const tower of towers) {
      seen.add(tower.id);
      let container = this.towers.get(tower.id);
      if (!container) {
        this.effects.spawnUpgradeFlash(tower.position.x, tower.position.y);
        container = this.createTowerVisual(tower);
        this.towers.set(tower.id, container);
        // 初始化攻速加成指示器
        this.updateSkillIndicator(container, tower);
      } else {
        const prevLevel = this.readTowerLevel(container);
        if (tower.level > (prevLevel ?? tower.level)) {
          if (!this.upgradedTowers.has(tower.id)) {
            this.effects.spawnUpgradeFlash(tower.position.x, tower.position.y);
            this.upgradedTowers.add(tower.id);
          }
          this.writeTowerLevel(container, tower.level);
        }
        container.setPosition(tower.position.x, tower.position.y);
        // 更新攻速加成指示器
        this.updateSkillIndicator(container, tower);
      }
    }
    for (const [id, container] of this.towers) {
      if (!seen.has(id)) {
        container.destroy();
        this.towers.delete(id);
        this.towerSkillGlows.get(id)?.destroy();
        this.towerSkillGlows.delete(id);
        this.towerSkillReady.delete(id);
        const circle = this.towerRangeCircles.get(id);
        circle?.destroy();
        this.towerRangeCircles.delete(id);
        this.upgradedTowers.delete(id);
        this.towerBodyRadii.delete(id);
      }
    }
  }

  /** 从塔的文本子对象读取等级编号。 */
  private readTowerLevel(container: Phaser.GameObjects.Container): number | null {
    for (const child of container.list) {
      const textObj = child as Phaser.GameObjects.Text;
      if (textObj && typeof textObj.text === "string") {
        const n = parseInt(textObj.text, 10);
        if (!isNaN(n)) return n;
      }
    }
    return null;
  }

  /** 更新塔容器内部的等级文本。 */
  private writeTowerLevel(container: Phaser.GameObjects.Container, level: number): void {
    for (const child of container.list) {
      const textObj = child as Phaser.GameObjects.Text;
      if (textObj && typeof textObj.text === "string") {
        textObj.setText(String(level));
        break;
      }
    }
  }

  private reconcileLaserBeams(towers: TowerState[], enemies: EnemyState[], gameTime: number): void {
    const visibleBeams = new Set<string>();
    for (const tower of towers) {
      const config = this.towersConfig[tower.type];
      const target = tower.laserTargetId
        ? enemies.find(
            (enemy) =>
              enemy.id === tower.laserTargetId && enemy.alive && !enemy.markedDead && enemy.hp > 0,
          )
        : undefined;
      if (config?.attackMode !== "continuous_laser" || !target) continue;

      visibleBeams.add(tower.id);
      let visual = this.laserBeams.get(tower.id);
      if (!visual) {
        visual = { graphics: this.add.graphics().setDepth(4), phase: 0 };
        this.laserBeams.set(tower.id, visual);
      }
      const enhanced =
        tower.level >= config.maxLevel &&
        (tower.laserPulseCount ?? 0) >= (config.laserEnhancement?.pulseCount ?? Infinity);
      const graphics = visual.graphics;
      const start = tower.position;
      const end = target.position;
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const distance = Math.hypot(dx, dy);
      const ux = distance > 0 ? dx / distance : 1;
      const uy = distance > 0 ? dy / distance : 0;
      const px = -uy;
      const py = ux;
      const pulse = 0.5 + 0.5 * Math.sin(gameTime * 8);
      const phase = gameTime * 2.4;
      const outerColor = enhanced ? 0xc4b5fd : 0x8b5cf6;
      const energyColor = enhanced ? 0xa5f3fc : 0xc4b5fd;
      const coreColor = enhanced ? 0xffffff : 0xf5edff;

      graphics.clear();
      // 多层光束以宽软外晕托出彩色能量柱和锐利亮芯，避免读成单一细线。
      graphics.lineStyle(enhanced ? 28 : 16, outerColor, 0.12 + pulse * 0.08);
      graphics.beginPath();
      graphics.moveTo(start.x, start.y);
      graphics.lineTo(end.x, end.y);
      graphics.strokePath();
      graphics.lineStyle(enhanced ? 17 : 8, outerColor, 0.24 + pulse * 0.12);
      graphics.beginPath();
      graphics.moveTo(start.x, start.y);
      graphics.lineTo(end.x, end.y);
      graphics.strokePath();
      graphics.lineStyle(enhanced ? 8 : 4, energyColor, 0.78 + pulse * 0.16);
      graphics.beginPath();
      graphics.moveTo(start.x, start.y);
      graphics.lineTo(end.x, end.y);
      graphics.strokePath();
      graphics.lineStyle(enhanced ? 2 : 1.5, coreColor, 0.98);
      graphics.beginPath();
      graphics.moveTo(start.x, start.y);
      graphics.lineTo(end.x, end.y);
      graphics.strokePath();

      // 发射端的奥术聚焦环与径向符文由模拟时间驱动，暂停时冻结。
      const emitterX = start.x + ux * 16;
      const emitterY = start.y + uy * 16;
      const ringRadius = enhanced ? 13 : 10;
      graphics.fillStyle(outerColor, 0.12 + pulse * 0.08);
      graphics.fillCircle(emitterX, emitterY, (enhanced ? 8 : 6) + pulse * 2);
      graphics.lineStyle(2.2, coreColor, 0.85);
      graphics.strokeCircle(emitterX, emitterY, ringRadius + pulse * 1.5);
      graphics.lineStyle(1.2, outerColor, 0.9);
      graphics.beginPath();
      graphics.moveTo(emitterX + px * (ringRadius + 3), emitterY + py * (ringRadius + 3));
      graphics.lineTo(emitterX - px * (ringRadius + 3), emitterY - py * (ringRadius + 3));
      graphics.strokePath();
      for (let glyph = 0; glyph < 4; glyph++) {
        const angle = phase + (Math.PI * 2 * glyph) / 4;
        const inner = ringRadius + 2;
        const outer = ringRadius + 6;
        graphics.lineStyle(1.4, coreColor, 0.76);
        graphics.beginPath();
        graphics.moveTo(emitterX + Math.cos(angle) * inner, emitterY + Math.sin(angle) * inner);
        graphics.lineTo(emitterX + Math.cos(angle) * outer, emitterY + Math.sin(angle) * outer);
        graphics.strokePath();
      }

      // 亮段以前亮后淡的拖尾向目标推进，固定绘制数量且不创建逐帧对象。
      if (distance > 18) {
        const streakCount = Math.min(4, Math.max(2, Math.floor(distance / 56)));
        const streakLength = Math.min(24, distance * 0.18);
        const streakRatio = streakLength / distance;
        for (let streak = 0; streak < streakCount; streak++) {
          const t = (phase * 0.48 + streak / streakCount) % 1;
          const tailT = 0.06 + t * 0.78;
          const headT = Math.min(tailT + streakRatio, 0.94);
          const tailX = start.x + dx * tailT;
          const tailY = start.y + dy * tailT;
          const headX = start.x + dx * headT;
          const headY = start.y + dy * headT;
          graphics.lineStyle(enhanced ? 8 : 4, energyColor, 0.32 + pulse * 0.12);
          graphics.beginPath();
          graphics.moveTo(tailX, tailY);
          graphics.lineTo(headX, headY);
          graphics.strokePath();
          graphics.lineStyle(enhanced ? 4 : 2, coreColor, 0.76 + pulse * 0.24);
          graphics.beginPath();
          graphics.moveTo(tailX + dx * streakRatio * 0.45, tailY + dy * streakRatio * 0.45);
          graphics.lineTo(headX, headY);
          graphics.strokePath();
          graphics.fillStyle(coreColor, 0.9 + pulse * 0.1);
          graphics.fillCircle(headX, headY, enhanced ? 3.2 : 2.6);
        }
      }

      // 发射端到目标端的能量微粒加快推进，数量固定且有上限。
      if (distance > 8) {
        const particleCount = Math.min(6, Math.max(2, Math.floor(distance / 42)));
        for (let particle = 0; particle < particleCount; particle++) {
          const t = ((phase * 1.8 + particle / particleCount) % 1) * 0.86 + 0.07;
          const wobble = Math.sin(phase * 2 + particle * 1.7) * 2.5;
          const x = start.x + dx * t + px * wobble;
          const y = start.y + dy * t + py * wobble;
          const radius = particle % 2 === 0 ? 2.4 : 1.6;
          graphics.fillStyle(coreColor, 0.8 + 0.2 * Math.sin(phase + particle));
          graphics.fillCircle(x, y, radius);
        }
      }

      // 目标端的脉动聚焦环表现能量落点，不额外创建独立特效对象。
      const impactRadius = (enhanced ? 7 : 5) + pulse * 1.5;
      graphics.fillStyle(outerColor, 0.16 + pulse * 0.08);
      graphics.fillCircle(end.x, end.y, impactRadius + 2);
      graphics.lineStyle(1.8, coreColor, 0.82 + pulse * 0.16);
      graphics.strokeCircle(end.x, end.y, impactRadius);
      graphics.lineStyle(1, energyColor, 0.76);
      graphics.beginPath();
      graphics.moveTo(end.x - px * (impactRadius + 3), end.y - py * (impactRadius + 3));
      graphics.lineTo(end.x + px * (impactRadius + 3), end.y + py * (impactRadius + 3));
      graphics.strokePath();
    }

    for (const [towerId, visual] of this.laserBeams) {
      if (visibleBeams.has(towerId)) continue;
      visual.graphics.destroy();
      this.laserBeams.delete(towerId);
    }
  }

  private createTowerVisual(tower: TowerState): Phaser.GameObjects.Container {
    const color =
      tower.type === "magic_tower"
        ? 0xa855f7
        : tower.type === "cannon"
          ? 0x64748b
          : tower.type === "arrow"
            ? 0x38bdf8
            : 0xffffff;
    const radiusCells = this.towersConfig[tower.type]?.bodyRadiusCells ?? 0.5;
    const bodyRadius = (radiusCells * this.mapConfig.gridSize) / 2;
    this.towerBodyRadii.set(tower.id, bodyRadius);
    const textureKey = this.towerSkinTextureKeys[tower.type];
    const body =
      textureKey && this.textures.exists(textureKey)
        ? this.add.image(0, 0, textureKey)
        : this.add.circle(0, 0, bodyRadius, color, 1);
    if (body instanceof Phaser.GameObjects.Image) {
      body.setScale((bodyRadius * 4) / body.height);
    } else {
      body.setStrokeStyle(2, 0x0f172a, 1);
    }
    const visualHalfHeight =
      body instanceof Phaser.GameObjects.Image ? body.displayHeight / 2 : bodyRadius;
    const levelTextOffset = visualHalfHeight + 8;
    const levelText = this.add
      .text(0, levelTextOffset, String(tower.level), {
        fontSize: "12px",
        color: "#93c5fd",
        fontFamily: "monospace",
      })
      .setOrigin(0.5);
    const container = this.add
      .container(tower.position.x, tower.position.y, [body, levelText])
      .setDepth(0);
    const range = this.towersConfig[tower.type]?.range ?? 100;
    const circle = this.add.circle(tower.position.x, tower.position.y, range, 0x60a5fa, 0.08);
    circle.setStrokeStyle(1, 0x60a5fa, 0.5);
    circle.setVisible(false);
    this.towerRangeCircles.set(tower.id, circle);
    return container;
  }

  // ─── 投射物协调 ──────────────────────────────────────────────

  private reconcileProjectiles(state: GameState): void {
    const seen = new Set<string>();
    const currentProjectiles = new Map<
      string,
      {
        position: Point;
        attackMode?: ProjectileState["attackMode"];
        impactRadius?: number;
      }
    >();
    for (const projectile of state.projectiles) {
      seen.add(projectile.id);
      let dot = this.projectiles.get(projectile.id);
      if (!dot) {
        dot =
          this.projectilePool.length > 0
            ? this.projectilePool.pop()!
            : this.add.circle(0, 0, GameScene.PROJECTILE_RADIUS, 0xfde68a, 1);
        dot.setStrokeStyle(1, 0x0f172a, 1);
        dot.setVisible(true);
        this.projectiles.set(projectile.id, dot);
      }
      const isIceConeSkill = projectile.attackMode === "ice_cone";
      // 冰塔普通子弹统一为冰蓝色；技能大冰锥更大并带冰晶拖尾
      const sourceTower = state.towers.find((t) => t.id === projectile.sourceTowerId);
      const isIceTower = sourceTower?.type === "ice_tower";
      if (isIceConeSkill || isIceTower) {
        dot.setFillStyle(0x7dd3fc, 1);
        dot.setStrokeStyle(2, 0xcffafe, 1);
      } else {
        dot.setFillStyle(0xfde68a, 1);
        dot.setStrokeStyle(1, 0x0f172a, 1);
      }
      dot.setRadius(
        isIceConeSkill ? GameScene.ICE_CONE_PROJECTILE_RADIUS : GameScene.PROJECTILE_RADIUS,
      );
      dot.setPosition(projectile.position.x, projectile.position.y);
      currentProjectiles.set(projectile.id, {
        position: { x: projectile.position.x, y: projectile.position.y },
        attackMode: projectile.attackMode,
        impactRadius: projectile.impactControl?.radius,
      });
      // 为技能大冰锥绘制冰晶拖尾轨迹；其他子弹清除旧轨迹避免残留
      if (isIceConeSkill) {
        const prev = this.previousProjectiles.get(projectile.id);
        if (prev && prev.position) {
          let trail = this.iceConeTrails.get(projectile.id);
          if (!trail) {
            trail =
              this.iceConeTrailPool.length > 0 ? this.iceConeTrailPool.pop()! : this.add.graphics();
            trail.setDepth(5);
            trail.clear();
          }
          trail.clear();
          const fromX = prev.position.x;
          const fromY = prev.position.y;
          const toX = projectile.position.x;
          const toY = projectile.position.y;
          trail.lineStyle(3, 0x7dd3fc, 0.8);
          trail.beginPath();
          trail.moveTo(fromX, fromY);
          trail.lineTo(toX, toY);
          trail.strokePath();
          const midX = (fromX + toX) / 2;
          const midY = (fromY + toY) / 2;
          trail.fillStyle(0xcffafe, 1);
          trail.fillCircle(midX, midY, 2);
          trail.fillStyle(0x38bdf8, 1);
          trail.fillCircle(fromX, fromY, 1.5);
          this.iceConeTrails.set(projectile.id, trail);
        }
      } else {
        const trail = this.iceConeTrails.get(projectile.id);
        if (trail) {
          trail.clear();
          this.iceConeTrails.delete(projectile.id);
          if (this.iceConeTrailPool.length < GameScene.PROJECTILE_POOL_BUDGET) {
            this.iceConeTrailPool.push(trail);
          }
        }
      }
    }
    for (const [id, dot] of this.projectiles) {
      if (!seen.has(id)) {
        const previous = this.previousProjectiles.get(id);
        if (
          previous?.attackMode === "ice_cone" &&
          previous.impactRadius !== undefined &&
          Number.isFinite(previous.impactRadius) &&
          previous.impactRadius > 0
        ) {
          this.effects.spawnIceConeImpact(
            previous.position.x,
            previous.position.y,
            previous.impactRadius,
          );
        }
        dot.setVisible(false);
        this.projectiles.delete(id);
        if (this.projectilePool.length < GameScene.PROJECTILE_POOL_BUDGET) {
          this.projectilePool.push(dot);
        }
        // 冰锥命中后销毁对应的轨迹图形，避免残留
        const trail = this.iceConeTrails.get(id);
        if (trail) {
          trail.destroy();
          this.iceConeTrails.delete(id);
        }
      }
    }
    this.previousProjectiles.clear();
    for (const [id, snapshot] of currentProjectiles) {
      this.previousProjectiles.set(id, snapshot);
    }
  }

  destroyScene(): void {
    if (this.input) {
      this.input.off("pointermove", this.onGhostMove);
      this.input.setDefaultCursor("default");
    }
    this.placementGhost?.destroy();
    this.placementGhost = null;
    this.gridOverlay?.forEach((r) => r.destroy());
    this.gridOverlay = null;
    this.enemySelectionCircle?.destroy();
    this.enemySelectionCircle = null;
    this.towerActionButtons.forEach((c) => c.destroy());
    this.towerActionButtons.clear();
    this.towerButtonHitZones.clear();
    this.enemies.clear();
    this.enemyBodyRadii.clear();
    this.enemyHpBars.clear();
    this.enemyHpBarBgs.clear();
    this.towers.clear();
    this.laserBeams.forEach((visual) => visual.graphics.destroy());
    this.laserBeams.clear();
    this.towerRangeCircles.clear();
    this.towerSkillGlows.forEach((v) => v.destroy());
    this.towerSkillGlows.clear();
    this.towerSkillReady.clear();
    this.damageTypesByEnemy.clear();
    for (const dot of this.projectiles.values()) dot.destroy();
    this.projectiles.clear();
    for (const trail of this.iceConeTrails.values()) trail.destroy();
    this.iceConeTrails.clear();
    this.iceConeTrailPool.length = 0;
    this.previousProjectiles.clear();
    this.inputAttached = false;
    this.sceneCreated = false;
  }
}
